// Core game types and enums
export type PlayerId = string;
export type GameId = string;
export type ThemeId = string;

export enum GameMode {
  SINGLE_PLAYER = 'single_player',
  MULTIPLAYER = 'multiplayer'
}

export enum PlayerType {
  HUMAN = 'human',
  AI = 'ai'
}

export enum AIDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export enum GameStatus {
  WAITING = 'waiting',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ABANDONED = 'abandoned'
}

export enum Mark {
  X = 'X',
  O = 'O'
}

export enum LobbyStatus {
  WAITING = 'waiting',
  FULL = 'full',
  IN_PROGRESS = 'in_progress'
}

export enum AuthMethod {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  APPLE = 'apple',
  YAHOO = 'yahoo',
  CUSTOM = 'custom',
  GUEST = 'guest'
}

// Position on the game board
export interface Position {
  row: number;
  col: number;
}

// Individual cell on the game board
export interface Cell {
  position: Position;
  mark: Mark | null;
  playerId: PlayerId | null;
}

// Game board representation
export interface Board {
  cells: Cell[][];
  size: number;
}

// Player information
export interface Player {
  id: PlayerId;
  name: string;
  avatar?: string;
  type: PlayerType;
  difficulty?: AIDifficulty;
}

// Game settings configuration
export interface GameSettings {
  mode: GameMode;
  timeLimit?: number;
  theme: ThemeId;
  soundEnabled: boolean;
  realTimeMode: boolean;
}

// Complete game session
export interface GameSession {
  id: GameId;
  mode: GameMode;
  players: Player[];
  board: Board;
  currentPlayer: PlayerId;
  status: GameStatus;
  createdAt: Date;
  settings: GameSettings;
}

// Result of a move attempt
export interface MoveResult {
  success: boolean;
  gameState?: GameState;
  error?: string;
  winResult?: WinResult;
}

// Current state of the game
export interface GameState {
  board: Board;
  currentPlayer: PlayerId;
  status: GameStatus;
  winner?: PlayerId;
  winningCells?: Position[];
}

// Win condition result
export interface WinResult {
  hasWinner: boolean;
  winner?: PlayerId;
  winningCells?: Position[];
  isDraw: boolean;
}

// Move representation
export interface Move {
  gameId: GameId;
  playerId: PlayerId;
  position: Position;
  timestamp: Date;
}

// Room code for multiplayer
export interface RoomCode {
  code: string;
  expiresAt: Date;
  gameId?: GameId;
}

// Lobby game listing
export interface LobbyGame {
  id: GameId;
  hostPlayer: Player;
  status: LobbyStatus;
  settings: GameSettings;
  createdAt: Date;
}

// User profile and statistics
export interface Profile {
  id: string;
  name: string;
  avatar?: string;
  email?: string;
  authMethod: AuthMethod;
  preferences: UserPreferences;
  statistics: GameStatistics;
  createdAt: Date;
}

export interface GameStatistics {
  singlePlayer: {
    [key in AIDifficulty]: {
      wins: number;
      losses: number;
      draws: number;
    }
  };
  multiplayer: {
    wins: number;
    losses: number;
    draws: number;
  };
  totalGames: number;
  winRate: number;
}

export interface UserPreferences {
  theme: ThemeId;
  soundEnabled: boolean;
  defaultGameMode: GameMode;
  notifications: NotificationSettings;
}

export interface NotificationSettings {
  turnBased: boolean;
  gameInvites: boolean;
  gameResults: boolean;
}

// Authentication types
export interface Credentials {
  email?: string;
  password?: string;
  token?: string;
}

export interface AuthResult {
  success: boolean;
  profile?: Profile;
  token?: string;
  error?: string;
}

// Game result for statistics
export interface GameResult {
  gameId: string;
  winner?: string;
  isDraw: boolean;
  mode: string;
  difficulty?: string;
  duration: number;
  completedAt: Date;
}

// Callback types
export type MoveCallback = (move: Move) => void;